import {useMemo} from 'react';
import Grid from '@material-ui/core/Grid';
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import {Link, useHistory, useLocation} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Authorize, useUser} from '@postinumero/authorization';
import {useCorporateActionsEvents} from 'api';
import List from './List';
import {newCA} from 'App/Pages';
import Details from './Details';
import Filters from './Filters';
import {DONE, DRAFT, INVALID, PUBLISHED, READYTOPUBLISH} from 'shared/Statuses';
import {isMoment} from "moment";
import {getConfig} from "../../../config";

const formatDate = date => {
  if (isMoment(date)) {
    return date.format('YYYY-MM-DD');
  }
  if (typeof date === 'string' && date.length > 0) {
    return date;
  }
  return null;
}

const useParams = ({eligibleAgents, eligibleStatuses}) => {

  const location = useLocation();
  const history = useHistory();

  const filters = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    const search = sp.get('search') ?? '';
    const endDate = sp.get('endDate');
    const startDate = sp.get('startDate');
    let statuses = sp.getAll('statuses');
    if (statuses.length === 0) {
      statuses = [INVALID,PUBLISHED];
    }
    statuses = statuses.filter(v => eligibleStatuses.includes(v));

    let issuerAgents = sp.getAll('issuerAgents');
    if (issuerAgents.length === 0 && eligibleAgents.length === 1) {
      issuerAgents = eligibleAgents;
    }
    issuerAgents = issuerAgents.filter(v => eligibleAgents.includes(v)) ?? [];

    return {
      search, endDate, startDate, statuses, issuerAgents
    };
  }, [location.search, eligibleAgents, eligibleStatuses]);

  const params = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const ca = parseInt(params.get('ca'));
    return {ca, filters};
  }, [location.search, filters]);

  const setParams = ({ca, filters = {}}) => {
    const sp = new URLSearchParams();
    if (Number.isSafeInteger(ca)) {
      sp.set('ca', ca);
    }

    ['search', 'endDate', 'startDate'].forEach(name => {
      const value = filters[name];
      if (typeof value === 'string' && value.length > 0) {
        sp.set(name, value);
      }
    });

    ['statuses', 'issuerAgents'].forEach(name => {
      const value = filters[name];
      if (Array.isArray(value) && value.length > 0) {
        value.forEach(v => sp.append(name, v));
      }
    });

    const qs = sp.toString();
    if (qs !== location.search) {
      history.push({pathname: '/corporate-actions', search: qs});
    }
  };

  return [params, setParams];
};

const availableAgents = () => {
  let agents = getConfig('ORGANISATIONS');
  if (typeof agents !== 'string' || !agents.trim().length) {
    agents = 'Aktia, Alandsbanken, Danskebank, Evli, Nordea, Nordnet, Op, Saastopankki, Seb'
  }
  return agents.trim().split(/\s*,\s*/);
}

export default function Layout() {
  const [t] = useTranslation();

  const user = useUser();
  const isIA = user.roles.includes('Issuer agent');

  // FIXME: fetch dynamically
  const eligibleAgents = isIA ? [user.groups] : availableAgents();
  const eligibleStatuses = useMemo(() => [PUBLISHED, INVALID, DONE], []);

  const [params, setParams] = useParams({eligibleAgents, eligibleStatuses});

  const { isLoading, data: actionEvents } = useCorporateActionsEvents(params.filters);

  const selectedEvent = params.ca && actionEvents.find(item => item.id === params.ca) || undefined;
    //: actionEvents.find(item => item.organisation === user.groups);

  const handleChange = (e, isExpanded) => {
    const id = parseInt(e.target.closest('.MuiAccordionSummary-root').id);
    if (isExpanded && params.ca !== id) {
      setParams({...params, ca: id});
    } else if (!isExpanded && params.ca === id) {
      setParams({...params, ca: NaN});
    }
  };

  const resetFilter = () => {
    setParams({});
  };

  const setFilters = (filters) => {
    const { startDate, endDate } = filters;
    filters = {...filters, startDate: formatDate(startDate), endDate: formatDate(endDate)};
    setParams({...params, filters});
  }

  return (
    <Grid container spacing={5}>
      <Grid item xs={12}>
        <Typography variant="h3" paragraph>{t("filter.title")}</Typography>
        <Filters
          activeFilters={params.filters}
          setFilters={setFilters}
          statuses={eligibleStatuses}
          issuerAgents={eligibleAgents}
          reset={resetFilter}
        />
      </Grid>
      <Grid item xs={12} md={7} lg={8}>
        { isLoading && (<span><CircularProgress size='1rem'/> Loading ...</span>)}
        { !isLoading && actionEvents &&
          <List corporateActions={actionEvents} handleChange={handleChange} expanded={selectedEvent?.id}/>
        }
      </Grid>
      <Grid item xs={12} md={5} lg={4}>
        <Details corporateAction={selectedEvent} show={selectedEvent?.id}/>
      </Grid>
      <Authorize allow="Issuer agent">
        <Grid item sm={12}>
          <Button variant="contained" color="secondary" size="large" component={Link}
                  to={newCA}>{t("events.addNew")}</Button>
        </Grid>
      </Authorize>
    </Grid>
  );
}
